import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { fetchReferralByCode } from "../actions/referralActions";
import formatMoney from "../components/formatMoney";
import { QRCode } from "react-qr-code";
import {
  FaLink,
  FaUserPlus,
  FaCopy,
  FaCheck,
  FaChartLine,
  FaMoneyBillWave,
  FaShoppingCart,
  FaPercent,
  FaWallet,
  FaArrowUp,
  FaArrowDown,
  FaQrcode,
  FaFileInvoiceDollar,
  FaHistory,
} from "react-icons/fa";
import "../styles/referralDashboard.css";
import { ReferralDashboardSkeleton } from "../components/HomeSkeletons";
import { getReferralLink, SITE_NAME_FULL } from "../variables";

const ReferralDashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const user = useSelector((state) => state.auth.user);
  const [referralCode, setReferralCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [referralData, setReferralData] = useState(null);
  const [error, setError] = useState(null);
  const [hasAutoLoaded, setHasAutoLoaded] = useState(false);

  const { referralData: reduxReferralData, loading } = useSelector(
    (state) => state.referral || {}
  );

  useEffect(() => {
    document.title = `Referral Dashboard - ${SITE_NAME_FULL}`;
  }, []);

  useEffect(() => {
    if (reduxReferralData) {
      setReferralData(reduxReferralData);
      setIsLoading(false);
      setError(null);
    }
  }, [reduxReferralData]);

  // Auto-load referral data from URL query parameter
  useEffect(() => {
    const codeFromUrl = searchParams.get("code");

    if (codeFromUrl && !hasAutoLoaded) {
      const trimmedCode = codeFromUrl.trim();
      setReferralCode(trimmedCode);
      setHasAutoLoaded(true);

      // Automatically fetch the referral data
      setIsLoading(true);
      setError(null);
      setReferralData(null);

      dispatch(fetchReferralByCode(trimmedCode))
        .then((data) => {
          setReferralData(data);
          setIsLoading(false);
        })
        .catch((err) => {
          setError(err.message || "Failed to fetch referral data");
          setReferralData(null);
          setIsLoading(false);
        });
    }
  }, [searchParams, dispatch, hasAutoLoaded]);

  const handleSearch = async (e) => {
    e.preventDefault();

    if (!referralCode.trim()) {
      setError("Please enter a referral code");
      return;
    }

    setIsLoading(true);
    setError(null);
    setReferralData(null);

    try {
      const data = await dispatch(fetchReferralByCode(referralCode.trim()));
      setReferralData(data);
    } catch (err) {
      setError(err.message || "Failed to fetch referral data");
      setReferralData(null);
    } finally {
      setIsLoading(false);
    }
  };

  const downloadQRCode = () => {
    if (!referralData || !referralData.code) {
      alert("Referral data not available. Please try again.");
      return;
    }

    try {
      const qrWrapper = document.getElementById("qrcode");
      if (!qrWrapper) {
        console.error("QR code element not found");
        alert("QR code not found. Please ensure the QR code is displayed.");
        return;
      }

      // Find the SVG element inside the wrapper
      const svg = qrWrapper.querySelector("svg");
      if (!svg) {
        console.error("SVG element not found");
        alert("QR code SVG not found. Please refresh the page and try again.");
        return;
      }

      // Clone the SVG to avoid modifying the original
      const svgClone = svg.cloneNode(true);

      // Set explicit dimensions on the clone
      svgClone.setAttribute("width", "220");
      svgClone.setAttribute("height", "220");
      svgClone.setAttribute("xmlns", "http://www.w3.org/2000/svg");

      const svgData = new XMLSerializer().serializeToString(svgClone);
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      const img = new Image();

      // Set canvas size
      canvas.width = 220;
      canvas.height = 220;

      img.onload = () => {
        ctx.drawImage(img, 0, 0);
        const pngFile = canvas.toDataURL("image/png");
        const downloadLink = document.createElement("a");
        downloadLink.download = `referral-qr-${referralData.code}.png`;
        downloadLink.href = pngFile;
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
      };

      img.onerror = (error) => {
        console.error("Error loading image:", error);
        // Fallback: try direct SVG download
        const svgBlob = new Blob([svgData], { type: "image/svg+xml" });
        const url = URL.createObjectURL(svgBlob);
        const downloadLink = document.createElement("a");
        downloadLink.download = `referral-qr-${referralData.code}.svg`;
        downloadLink.href = url;
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
        URL.revokeObjectURL(url);
      };

      img.src =
        "data:image/svg+xml;base64," +
        btoa(unescape(encodeURIComponent(svgData)));
    } catch (error) {
      console.error("Error downloading QR code:", error);
      alert("Failed to download QR code. Please try again.");
    }
  };

  const referralLink =
    referralData?.link ||
    (referralCode ? getReferralLink(referralCode) : "");

  const [copied, setCopied] = useState(false);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="referral-dashboard">
      <div className="referral-dashboard-container">
        <div className="dashboard-header">
          <div className="header-content">
            <h1 className="referral-dashboard-title">
              <FaChartLine className="title-icon" />
              Referral Dashboard
            </h1>
            <p className="dashboard-subtitle">
              Track your referral performance and earnings
            </p>
          </div>
          {user && user.user_id && (
            <button
              onClick={() => navigate("/create-referral")}
              className="create-referral-btn-header"
            >
              <FaUserPlus /> Create New Referral
            </button>
          )}
        </div>

        {user && user.user_id && (
          <div className="referral-dashboard-actions">
            <div className="info-banner">
              <FaLink className="link-icon" />
              <div className="banner-content">
                <strong>Check your earnings here!</strong>
                <span>
                  Enter your referral code below to view your statistics and
                  earnings.
                </span>
              </div>
            </div>
          </div>
        )}

        <div className="referral-search-section">
          <div className="search-card">
            <form onSubmit={handleSearch} className="referral-search-form">
              <div className="search-input-wrapper">
                <input
                  type="text"
                  value={referralCode}
                  onChange={(e) => setReferralCode(e.target.value)}
                  placeholder="Enter referral code to view analytics"
                  className="referral-code-input"
                />
                <button
                  type="submit"
                  disabled={isLoading}
                  className="referral-search-button"
                >
                  {isLoading ? (
                    <span className="loading-spinner"></span>
                  ) : (
                    <>
                      <FaChartLine /> Search
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>

        {error && (
          <div className="referral-error">
            <span className="error-icon">⚠</span>
            {error}
          </div>
        )}

        {isLoading && !referralData && <ReferralDashboardSkeleton />}

        {referralData && (
          <div className="referral-dashboard-content">
            {/* Summary Stats Cards */}
            <div className="referral-summary-section">
              <div className="summary-cards">
                <div className="summary-card stat-card orders">
                  <div className="stat-icon">
                    <FaShoppingCart />
                  </div>
                  <div className="stat-content">
                    <h3>Total Orders</h3>
                    <p className="summary-value">
                      {referralData.total_orders || 0}
                    </p>
                  </div>
                </div>
                <div className="summary-card stat-card revenue">
                  <div className="stat-icon">
                    <FaMoneyBillWave />
                  </div>
                  <div className="stat-content">
                    <h3>Total Revenue</h3>
                    <p className="summary-value">
                      {formatMoney(referralData.total_revenue || 0)}
                    </p>
                  </div>
                </div>
                <div className="summary-card stat-card percentage">
                  <div className="stat-icon">
                    <FaPercent />
                  </div>
                  <div className="stat-content">
                    <h3>Commission Rate</h3>
                    <p className="summary-value">
                      {referralData.percentage || 0}%
                    </p>
                  </div>
                </div>
                <div className="summary-card stat-card earned highlight-card">
                  <div className="stat-icon">
                    <FaWallet />
                  </div>
                  <div className="stat-content">
                    <h3>Total Earned</h3>
                    <p className="summary-value highlight">
                      {formatMoney(referralData.total_amount || 0)}
                    </p>
                  </div>
                </div>
                <div className="summary-card stat-card paid success-card">
                  <div className="stat-icon">
                    <FaArrowDown />
                  </div>
                  <div className="stat-content">
                    <h3>Total Paid</h3>
                    <p className="summary-value">
                      {formatMoney(referralData.total_paid || 0)}
                    </p>
                  </div>
                </div>
                <div className="summary-card stat-card remaining">
                  <div className="stat-icon">
                    <FaArrowUp />
                  </div>
                  <div className="stat-content">
                    <h3>Remaining Balance</h3>
                    <p
                      className="summary-value"
                      style={{
                        color:
                          parseFloat(referralData.remaining_amount || 0) > 0
                            ? "#17a2b8"
                            : "#6c757d",
                      }}
                    >
                      {formatMoney(referralData.remaining_amount || 0)}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Referral Info and QR Code */}
            <div className="referral-info-section">
              <div className="referral-card info-card">
                <div className="card-header">
                  <h2>
                    <FaChartLine /> Referral Information
                  </h2>
                </div>
                <div className="referral-info-grid">
                  <div className="info-item">
                    <span className="info-label">Referral Code</span>
                    <span className="info-value code-value">
                      {referralData.code}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Name</span>
                    <span className="info-value">{referralData.name}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Status</span>
                    <span
                      className={`status-badge-info ${referralData.status}`}
                    >
                      {referralData.status}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Commission Rate</span>
                    <span className="info-value percentage-value">
                      {referralData.percentage || 0}%
                    </span>
                  </div>
                </div>
                <div className="referral-link-section">
                  <label>Your Referral Link</label>
                  <div className="link-copy-wrapper">
                    <input
                      type="text"
                      value={referralLink}
                      readOnly
                      className="referral-link-input"
                    />
                    <button
                      onClick={() => copyToClipboard(referralLink)}
                      className="copy-link-btn"
                      title="Copy link"
                    >
                      {copied ? <FaCheck /> : <FaCopy />}
                    </button>
                  </div>
                </div>
              </div>

              <div className="referral-card qr-card">
                <div className="card-header">
                  <h2>
                    <FaQrcode /> QR Code
                  </h2>
                </div>
                <div className="qr-code-container">
                  <div id="qrcode" className="qr-wrapper">
                    <QRCode
                      value={referralLink}
                      size={220}
                      style={{
                        height: "auto",
                        maxWidth: "100%",
                        width: "100%",
                      }}
                      viewBox={`0 0 220 220`}
                    />
                  </div>
                </div>
                <button onClick={downloadQRCode} className="download-qr-button">
                  <FaQrcode /> Download QR Code
                </button>
              </div>
            </div>

            <div className="referral-payouts-section">
              <div className="section-header">
                <h2>
                  <FaFileInvoiceDollar /> Payout Transactions
                </h2>
                <span className="section-badge">
                  {referralData.payouts?.length || 0} transactions
                </span>
              </div>
              {referralData.payouts && referralData.payouts.length > 0 ? (
                <div className="payouts-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Amount</th>
                        <th>Payment Method</th>
                        <th>Transaction ID</th>
                        <th>Status</th>
                        <th>Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {referralData.payouts.map((payout) => (
                        <tr key={payout.payout_id}>
                          <td>
                            {new Date(payout.created_at).toLocaleDateString()}
                          </td>
                          <td className="payout-amount">
                            {formatMoney(payout.amount || 0)}
                          </td>
                          <td>{payout.payment_method || "-"}</td>
                          <td>{payout.transaction_id || "-"}</td>
                          <td>
                            <span
                              className={`payout-status-badge ${
                                payout.status === "completed"
                                  ? "completed"
                                  : payout.status === "failed"
                                  ? "failed"
                                  : "pending"
                              }`}
                            >
                              {payout.status}
                            </span>
                          </td>
                          <td>{payout.notes || "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="no-payouts">
                  <p>No payout transactions found.</p>
                </div>
              )}
            </div>

            <div className="referral-orders-section">
              <div className="section-header">
                <h2>
                  <FaHistory /> Orders List
                </h2>
                <span className="section-badge">
                  {referralData.orders?.length || 0} orders
                </span>
              </div>
              {referralData.orders && referralData.orders.length > 0 ? (
                <div className="orders-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Order ID</th>
                        <th>Date</th>
                        <th>Total Products</th>
                        <th>Total Amount</th>
                        <th>Percentage</th>
                        <th>Commission</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {referralData.orders.map((order) => (
                        <tr key={order.order_id}>
                          <td>{order.order_id}</td>
                          <td>
                            {new Date(order.created_at).toLocaleDateString()}
                          </td>
                          <td>{order.total_products}</td>
                          <td>
                            {formatMoney(
                              order.final_total || order.total_price || 0
                            )}
                          </td>
                          <td className="percentage-cell">
                            <span className="percentage-badge">
                              {order.referral_percentage_used !== undefined
                                ? order.referral_percentage_used.toFixed(2)
                                : 10.0}
                              %
                            </span>
                          </td>
                          <td className="commission-amount">
                            {formatMoney(order.commission_amount || 0)}
                          </td>
                          <td>
                            <span
                              className={`status-badge ${
                                order.payment_status === "Paid"
                                  ? "paid"
                                  : "unpaid"
                              }`}
                            >
                              {order.payment_status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="no-orders">
                  <p>No orders found for this referral code.</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReferralDashboard;
